import { createContext, type ReactNode, useContext, useEffect, useMemo, useRef, useState } from "react";

import { type SupportZendeskTicketModel } from "@doitintl/cmp-models";

import { useListZendeskTickets } from "../hooks/useListZendeskTickets";
import { useZendeskOrganization } from "../hooks/useZendeskOrganization";
import { type Organization } from "../types";

type ZendeskTicketsContextType = {
  zendeskTickets: SupportZendeskTicketModel[] | undefined;
  loading: boolean;
  error: Error | undefined;
  organization: Organization | undefined;
  organizationLoading: boolean;
  organizationError: Error | undefined;
  toggleOrganizationSharing: (organization: Organization) => void;
};

const ZendeskTicketsContext = createContext<ZendeskTicketsContextType | undefined>(undefined);

export function ZendeskTicketsContextProvider({ children }: { children: ReactNode }) {
  const [isSettled, setIsSettled] = useState(false);
  const timeoutRef = useRef<number>();

  const {
    data: organization,
    isLoading: organizationLoading,
    error: organizationError,
    toggleOrganizationSharing,
  } = useZendeskOrganization();

  const [tickets, loading, error] = useListZendeskTickets(organization?.shared_tickets ?? false);

  const filteredTickets = useMemo(
    () => (tickets ? tickets.filter((t) => !t.ticket.description.includes("created a new chat called")) : undefined),
    [tickets]
  );

  useEffect(() => {
    if (loading) {
      setIsSettled(false);
    } else {
      timeoutRef.current = window.setTimeout(() => {
        setIsSettled(true);
      }, 0);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [loading]);

  const contextValue = useMemo(
    () => ({
      zendeskTickets: filteredTickets,
      loading: loading || !isSettled,
      error,
      organization,
      organizationLoading,
      organizationError: organizationError as Error | undefined,
      toggleOrganizationSharing,
    }),
    [
      filteredTickets,
      loading,
      isSettled,
      error,
      organization,
      organizationLoading,
      organizationError,
      toggleOrganizationSharing,
    ]
  );

  return <ZendeskTicketsContext.Provider value={contextValue}>{children}</ZendeskTicketsContext.Provider>;
}

export function useZendeskTicketsContext() {
  const context = useContext(ZendeskTicketsContext);
  if (context === undefined) {
    throw new Error("useZendeskTickets must be used within a ZendeskTicketsProvider");
  }
  return context;
}
